import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AddSeatsAndInviteConfirmModal,
  BulkInviteCsvModal,
  ConfirmActionModalElement,
  type GroupConfirmKind,
  type GroupSubscriptionData,
  groupConfirmCopy,
  InviteByEmailModal,
  ManageSeatsModal,
  SelectMembersToRemoveAtRenewalModal,
} from '@shared/dream-components';

import useCopyToClipboard from '~/hooks/useCopyToClipboard';
import toast from '~/utils/toast';

import type {
  BulkSeatActionResult,
  CreateInvitationsResult,
  ValidateSeatsResult,
} from '../hooks/useGroupSubscriptions';

import { useCancelSubscription } from './useCancelSubscription';
import { useCsvUpload } from './useCsvUpload';
import { useReductionCandidates } from './useReductionCandidates';
import { useSeatAddition } from './useSeatAddition';
import { type SeatReductionResult, useSeatReduction } from './useSeatReduction';

export type BulkSeatActionHandler = (groupId: string, seatIds: string[]) => Promise<BulkSeatActionResult>;
export type CreateInvitationsHandler = (groupId: string, emails: string[]) => Promise<CreateInvitationsResult>;
export type ValidateSeatsHandler = (groupId: string, emails: string[]) => Promise<ValidateSeatsResult>;

export type UseGroupSubscriptionsModalsOptions = {
  /** Wired to POST /bulk_removes. Handles 1..N seat IDs uniformly so single-row
   *  remove uses the same code path as multi-select. */
  onBulkRemoveSeats?: BulkSeatActionHandler;
  /** Wired to POST /bulk_cancel_invites. Same 1..N contract as remove. */
  onBulkCancelInvites?: BulkSeatActionHandler;
  /** Wired to POST /bulk_resend_invitations. Same 1..N contract; BE branches
   *  internally on seat status (invited → resend, removed/declined → reinvite). */
  onBulkResendInvitations?: BulkSeatActionHandler;
  /** Wired by the consumer to the POST /invitations action. Used by both the
   *  single-invite modal and the within-capacity bulk-upload flow. */
  onCreateInvitations?: CreateInvitationsHandler;
  /** Wired to POST /seats/validation. The email-invite path preflights through
   *  this so the seat-addition math uses server-authoritative deduped emails +
   *  fresh remaining capacity, not the modal's client-side estimate (BEE-19928). */
  onValidateSeats?: ValidateSeatsHandler;
  /** Called after a successful mutation so the seats list refreshes. */
  onMutationSuccess?: () => void;
  /** Forwarded to invite modals so the inline "Manage seats" link is i18n'd.
   *  Same dict the consumer passes to `GroupSubscriptionsElement`. */
  translations?: Record<string, string>;
};

// One modal at a time across all groups (modals are full-screen overlays —
// stacking would be unreachable UX-wise). Each variant carries the groupId so
// the right group's data threads through, plus any captured form state.
type ModalState =
  | null
  | { kind: 'manageSeats'; groupId: string }
  | { kind: 'offPlatformManageSeats'; groupId: string }
  | { kind: 'inviteByEmail'; groupId: string }
  | { kind: 'bulkInviteCsv'; groupId: string }
  | { kind: 'addSeatsAndInvite'; groupId: string; emails: string[]; extraSeats: number }
  // Reduction picker. `mode` selects which backend call confirm makes:
  //   create — POST (no schedule yet) · lower — PATCH target+seats · swap — PATCH seats
  | {
      kind: 'selectMembersToRemove';
      groupId: string;
      targetSeatCount: number;
      deficit: number;
      mode: 'create' | 'lower' | 'swap';
    }
  | { kind: 'confirmCancelReduction'; groupId: string }
  | { kind: 'cancelSubscription'; groupId: string }
  | {
      kind: 'confirm';
      groupId: string;
      confirmKind: GroupConfirmKind;
      seatIds: string[];
      /** Captured at click; seats are server-paginated so a `find` from a
       *  passed-in groups array would miss off-page seats. */
      firstEmail: string | null;
    };

export type UseGroupSubscriptionsModalsResult = {
  openManageSeats: (groupId: string) => void;
  openInviteByEmail: (groupId: string) => void;
  openBulkInviteCsv: (groupId: string) => void;
  /** Opens the picker to swap which members lose access at renewal (banner
   *  "Modify" + each terminating row's "Manage"). */
  openModifyReduction: (groupId: string) => void;
  /** Confirms cancelling a scheduled reduction (banner "Cancel reduction"). */
  openCancelReduction: (groupId: string) => void;
  /** Opens the destructive confirm for canceling a group subscription outright. */
  openCancelSubscription: (groupId: string) => void;
  /** `firstEmail` lets confirm copy read "Remove foo@bar.com and N others?". */
  openConfirm: (groupId: string, confirmKind: GroupConfirmKind, seatIds: string[], firstEmail?: string | null) => void;
  /** Pre-rendered modal layer. Drop into JSX once at the consumer level —
   *  modals are full-screen so DOM position doesn't matter. */
  modals: ReactNode;
};

/**
 * Per-action toast copy. Centralized so single-row + bulk paths share the same
 * phrasing — pluralization + the "1 of N failed" partial message both flow
 * through here. Verb + noun + generic-fail messages differ per kind; counts and
 * error-tail formatting are uniform.
 */
const TOAST_COPY: Record<GroupConfirmKind, { past: string; noun: string; singleFail: string; generic: string }> = {
  remove: { past: 'removed', noun: 'seat', singleFail: 'Failed to remove seat', generic: 'Failed to remove seats' },
  'cancel-invite': {
    past: 'canceled',
    noun: 'invite',
    singleFail: 'Failed to cancel invite',
    generic: 'Failed to cancel invitations',
  },
  'resend-invite': {
    past: 'resent',
    noun: 'invite',
    singleFail: 'Failed to resend invite',
    generic: 'Failed to resend invitations',
  },
  reinvite: {
    past: 'sent',
    noun: 'invite',
    singleFail: 'Failed to send invite',
    generic: 'Failed to send invitations',
  },
};

function toastCopy(kind: GroupConfirmKind) {
  const { past, noun, singleFail, generic } = TOAST_COPY[kind];
  const tail = (err?: string) => (err ? ` — ${err}` : '');
  return {
    success: (n: number) => (n === 1 ? `1 ${noun} ${past}` : `${n} ${noun}s ${past}`),
    partial: (ok: number, fail: number, err?: string) => `${ok} ${past}, ${fail} failed${tail(err)}`,
    allFailed: (n: number, err?: string) => (n === 1 ? `${singleFail}${tail(err)}` : `${n} failures${tail(err)}`),
    failureGeneric: generic,
  };
}

/**
 * Format the per-email partial-failure toast. Rails returns each email's own
 * errors array; surface the first failed email + reason so the user knows
 * *which* invite didn't go through, not just that "some" failed.
 */
function formatPartialFailureMessage(result: CreateInvitationsResult): string {
  const failed = result.data?.invitations.filter((i) => !i.success) ?? [];
  if (failed.length === 0) return 'Some invitations could not be sent';
  const firstReason = failed[0].errors[0] ?? 'unknown error';
  if (failed.length === 1) {
    return `Couldn't invite ${failed[0].email}: ${firstReason}`;
  }
  return `Couldn't invite ${failed.length} addresses (${failed[0].email}: ${firstReason}, …)`;
}

export function useGroupSubscriptionsModals(
  groups: GroupSubscriptionData[],
  {
    onBulkRemoveSeats,
    onBulkCancelInvites,
    onBulkResendInvitations,
    onCreateInvitations,
    onValidateSeats,
    onMutationSuccess,
    translations,
  }: UseGroupSubscriptionsModalsOptions = {},
): UseGroupSubscriptionsModalsResult {
  const { t } = useTranslation('', { keyPrefix: 'manage.groups.card' });

  const [modal, setModal] = useState<ModalState>(null);
  const [isConfirmSubmitting, setIsConfirmSubmitting] = useState(false);
  const [isInviteSubmitting, setIsInviteSubmitting] = useState(false);
  const [isReductionSubmitting, setIsReductionSubmitting] = useState(false);
  const [isSeatAdditionSubmitting, setIsSeatAdditionSubmitting] = useState(false);
  const [isCancelSubscriptionSubmitting, setIsCancelSubscriptionSubmitting] = useState(false);
  const close = useCallback(() => setModal(null), []);

  const openManageSeats = useCallback(
    (groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (group?.payment_collection_method === 'off_platform') {
        setModal({ kind: 'offPlatformManageSeats', groupId });
        return;
      }
      setModal({ kind: 'manageSeats', groupId });
    },
    [groups],
  );
  const openInviteByEmail = useCallback((groupId: string) => setModal({ kind: 'inviteByEmail', groupId }), []);
  const openBulkInviteCsv = useCallback((groupId: string) => setModal({ kind: 'bulkInviteCsv', groupId }), []);
  const openConfirm = useCallback(
    (groupId: string, confirmKind: GroupConfirmKind, seatIds: string[], firstEmail: string | null = null) =>
      setModal({ kind: 'confirm', groupId, confirmKind, seatIds, firstEmail }),
    [],
  );

  // Over-capacity hand-off from the email/CSV modals: swap state in place,
  // carrying the typed/parsed emails + how many seats to add. The prior modal
  // unmounts when we replace state — no parent round-trip, no key gymnastics.
  // Guard against stacking a second purchase while one is awaiting payment —
  // the backend would 422 ("already pending"); surface that up front instead.
  const handleAddSeatsAndInvite = useCallback(
    (groupId: string, emails: string[], extraSeats: number) => {
      const group = groups.find((g) => g.id === groupId);
      if (group?.custom_deal) {
        toast.error("This group doesn't have enough open seats. Contact your publisher to add more seats.");
        return;
      }
      if (group?.pending_seat_addition != null) {
        toast.error('A seat addition is already pending payment');
        return;
      }
      setModal({ kind: 'addSeatsAndInvite', groupId, emails, extraSeats });
    },
    [groups],
  );

  // CSV upload state lives outside the presentation modal so the real upload
  // call sits next to the modal-transition logic. The hook never enters the
  // `review` step — Rails commits during upload, so we go upload→done on
  // success or upload→addSeatsAndInvite on overage.
  const csvUpload = useCsvUpload();
  const { clearUpload: clearCsvUpload, startUpload: startCsvUpload } = csvUpload;
  useEffect(() => {
    if (modal?.kind !== 'bulkInviteCsv') {
      clearCsvUpload();
    }
  }, [modal?.kind, clearCsvUpload]);

  const handleStartBulkUpload = useCallback(
    async (file: File) => {
      if (modal?.kind !== 'bulkInviteCsv') return;
      const result = await startCsvUpload(modal.groupId, file);
      if (result.kind === 'success') {
        const count = result.count;
        if (count > 0) {
          toast.success(count === 1 ? '1 invitation sent' : `${count} invitations sent`);
        }
        if (result.invalidRows.length > 0) {
          toast.error(
            `${result.invalidRows.length} ${result.invalidRows.length === 1 ? 'row was' : 'rows were'} skipped`,
          );
        }
        // Bulk upload creates seats server-side; refresh the index so the cards
        // reflect the new seat counts + invite-pending rows rather than the
        // pre-upload snapshot.
        onMutationSuccess?.();
        close();
      } else if (result.kind === 'overage') {
        // Rails did NOT create seats — hand off to the seat-purchase confirm
        // modal carrying the parsed valid_emails for after-purchase invite.
        // Same guard as the email path: don't stack a second pending purchase.
        const group = groups.find((g) => g.id === modal.groupId);
        if (group?.custom_deal) {
          toast.error("This group doesn't have enough open seats for everyone in the file.");
          close();
          return;
        }
        if (group?.pending_seat_addition != null) {
          toast.error('A seat addition is already pending payment');
          close();
          return;
        }
        setModal({
          kind: 'addSeatsAndInvite',
          groupId: modal.groupId,
          emails: result.validEmails,
          extraSeats: result.seatsNeeded,
        });
      } else if (result.kind === 'timeout') {
        // Not a failure — Sidekiq keeps sending invites after we stop polling.
        // Refresh in case some seats already landed before we gave up watching.
        toast(result.message);
        onMutationSuccess?.();
        close();
      }
      // 'error' is already surfaced by the upload state's banner — no toast.
    },
    [modal, startCsvUpload, close, onMutationSuccess, groups],
  );

  // ── Add seats and invite (over-capacity confirm) ───────────────────────────
  // The seat purchase is async: Rails fires a Stripe invoice and only bumps
  // capacity + invites the queued emails once `invoice.payment_succeeded` lands
  // (BEE-19902). So a 200 means "purchase initiated", not "seats live" — we
  // can't echo invited rows back. We toast that intent and refetch; the rows
  // appear whenever the webhook has landed by the next load.
  const { addSeats } = useSeatAddition();
  const handleAddSeatsAndInviteSubmit = useCallback(
    async (groupId: string, extraSeats: number, emails: string[]) => {
      setIsSeatAdditionSubmitting(true);
      // Rails persists the CSV on the SeatAddition record and re-parses it
      // after the Stripe webhook fires to invite the valid emails (BEE-19902).
      const csv = new Blob([['email', ...emails].join('\n')], { type: 'text/csv' });
      try {
        const result = await addSeats(groupId, extraSeats, csv);
        if (!result.ok) {
          // Keep the modal open so the admin can retry without re-uploading.
          toast.error(result.error ?? 'Failed to add seats');
          return;
        }
        const seatLabel = extraSeats === 1 ? 'seat' : 'seats';
        toast.success(`Adding ${extraSeats} ${seatLabel} — invites will be sent once your payment is confirmed`);
        onMutationSuccess?.();
        close();
      } finally {
        setIsSeatAdditionSubmitting(false);
      }
    },
    [addSeats, onMutationSuccess, close],
  );

  // ── Scheduled seat reduction ───────────────────────────────────────────────
  const { scheduleReduction, editReduction } = useSeatReduction();

  // Fetch the candidate pool only while the picker is open. We render `filled`
  // seats: those are the only seats the backend lets us (newly) terminate, and
  // for the swap flow `EditScheduledReduction` requires the new `seat_ids` to be
  // currently filled (it reverts the existing terminating set first).
  const pickerGroupId = modal?.kind === 'selectMembersToRemove' ? modal.groupId : null;
  const { seats: candidateSeats, isLoading: isLoadingCandidates } = useReductionCandidates(
    pickerGroupId,
    pickerGroupId != null,
  );
  // Swap shows all active members with the current terminating set pre-checked so
  // the admin toggles toward the target; create/lower only offer filled seats to
  // newly terminate. Backend swap is diff-based, so kept-terminating seat_ids are
  // valid alongside newly-picked filled ones.
  const pickerMode = modal?.kind === 'selectMembersToRemove' ? modal.mode : null;
  const reductionPool = useMemo(
    () => (pickerMode === 'swap' ? candidateSeats : candidateSeats.filter((s) => s.status === 'filled')),
    [candidateSeats, pickerMode],
  );
  const reductionInitialSelected = useMemo(
    () => (pickerMode === 'swap' ? candidateSeats.filter((s) => s.status === 'terminating').map((s) => s.id) : []),
    [candidateSeats, pickerMode],
  );

  const handleReductionResult = useCallback(
    (result: SeatReductionResult, successMessage: string) => {
      if (result.ok) {
        toast.success(successMessage);
        onMutationSuccess?.();
        close();
      } else {
        // Keep the modal open so the admin can adjust and retry.
        toast.error(result.error ?? 'Something went wrong');
      }
    },
    [onMutationSuccess, close],
  );

  // ManageSeats confirm WITHOUT a member pick: either adding seats (out of scope
  // here — handled by the seat-addition flow) or reducing only unfilled seats /
  // re-targeting an existing schedule (no specific members to pick).
  const handleManageSeatsSubmit = useCallback(
    async (groupId: string, targetSeatCount: number) => {
      const group = groups.find((g) => g.id === groupId);
      if (!group || targetSeatCount >= group.max_seats) {
        close();
        return;
      }
      const hasSchedule = group.scheduled_seat_count != null;
      setIsReductionSubmitting(true);
      try {
        const result = hasSchedule
          ? await editReduction(groupId, { targetSeatCount })
          : await scheduleReduction(groupId, targetSeatCount, []);
        handleReductionResult(result, hasSchedule ? 'Seat reduction updated' : 'Seat reduction scheduled');
      } finally {
        setIsReductionSubmitting(false);
      }
    },
    [groups, scheduleReduction, editReduction, handleReductionResult, close],
  );

  // ManageSeats handed off to the picker because the target drops below the
  // filled count — the admin must choose who loses access.
  const handleSelectMembersToRemove = useCallback(
    (groupId: string, targetSeatCount: number, deficit: number) => {
      const group = groups.find((g) => g.id === groupId);
      const mode = group?.scheduled_seat_count != null ? 'lower' : 'create';
      setModal({ kind: 'selectMembersToRemove', groupId, targetSeatCount, deficit, mode });
    },
    [groups],
  );

  const openModifyReduction = useCallback(
    (groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (!group || group.scheduled_seat_count == null) return;
      // No filled or terminating members means there's nothing to pick: the
      // reduction is only trimming unfilled seats, and the picker (which fetches
      // filled,terminating) would strand the admin on an empty list with a
      // "must remove N seats" dead end (BEE-20119). Route to Manage seats to
      // re-target the renewal count instead. Per Shreya we keep Modify enabled —
      // we just skip the member-selection step when there's no one to select.
      if (group.seats_filled === 0 && group.seats_terminating === 0) {
        openManageSeats(groupId);
        return;
      }
      // Swap keeps the same target; the admin re-picks which members terminate.
      setModal({
        kind: 'selectMembersToRemove',
        groupId,
        targetSeatCount: group.scheduled_seat_count,
        deficit: group.seats_terminating,
        mode: 'swap',
      });
    },
    [groups, openManageSeats],
  );

  const openCancelReduction = useCallback((groupId: string) => {
    setModal({ kind: 'confirmCancelReduction', groupId });
  }, []);

  const openCancelSubscription = useCallback((groupId: string) => {
    setModal({ kind: 'cancelSubscription', groupId });
  }, []);

  const { cancelSubscription } = useCancelSubscription();
  const handleConfirmCancelSubscription = useCallback(async () => {
    if (modal?.kind !== 'cancelSubscription') return;
    setIsCancelSubscriptionSubmitting(true);
    try {
      const result = await cancelSubscription(modal.groupId);
      if (result.ok) {
        toast.success('Subscription canceled.');
        onMutationSuccess?.();
        close();
      } else {
        toast.error(result.error ?? 'Failed to cancel subscription');
      }
    } finally {
      setIsCancelSubscriptionSubmitting(false);
    }
  }, [modal, cancelSubscription, onMutationSuccess, close]);

  const handlePickerConfirm = useCallback(
    async (seatIds: string[]) => {
      if (modal?.kind !== 'selectMembersToRemove') return;
      const { groupId, targetSeatCount, mode } = modal;
      setIsReductionSubmitting(true);
      try {
        let result: SeatReductionResult;
        if (mode === 'create') {
          result = await scheduleReduction(groupId, targetSeatCount, seatIds);
        } else if (mode === 'lower') {
          result = await editReduction(groupId, { targetSeatCount, seatIds });
        } else {
          result = await editReduction(groupId, { seatIds });
        }
        handleReductionResult(result, mode === 'create' ? 'Seat reduction scheduled' : 'Seat reduction updated');
      } finally {
        setIsReductionSubmitting(false);
      }
    },
    [modal, scheduleReduction, editReduction, handleReductionResult],
  );

  const handleConfirmCancelReduction = useCallback(async () => {
    if (modal?.kind !== 'confirmCancelReduction') return;
    setIsReductionSubmitting(true);
    try {
      const result = await editReduction(modal.groupId, { cancel: true });
      handleReductionResult(result, 'Scheduled reduction canceled');
    } finally {
      setIsReductionSubmitting(false);
    }
  }, [modal, editReduction, handleReductionResult]);

  const activeGroup = useMemo(() => {
    if (!modal || !('groupId' in modal)) return null;
    return groups.find((g) => g.id === modal.groupId) ?? null;
  }, [modal, groups]);

  // Check "not yet started" before `one_time`: a not-yet-started group can itself be one_time,
  // and has no filled seats to lose — the one_time copy would be misleading there.
  const cancelSubscriptionMessageKey = useMemo(() => {
    if (!activeGroup) return 'cancel_subscription_message';
    const notYetStarted =
      activeGroup.status === 'scheduled' &&
      !!activeGroup.term_starts_at &&
      new Date(activeGroup.term_starts_at) > new Date();
    if (notYetStarted) return 'cancel_subscription_message_scheduled';
    if (activeGroup.interval === 'one_time') return 'cancel_subscription_message_immediate';
    return 'cancel_subscription_message';
  }, [activeGroup]);

  const confirmCopy = useMemo(() => {
    if (modal?.kind !== 'confirm') return null;
    return groupConfirmCopy(modal.confirmKind, modal.seatIds.length, modal.firstEmail ?? '');
  }, [modal]);

  const offPlatformSeatsMessage = useMemo(() => {
    const email = activeGroup?.publication_contact_email ?? '';
    const message = t('off_platform_seats_message', {
      email,
      defaultValue:
        'Only the publisher can update the number of seats. Contact them at {{email}} to add or remove seats.',
    });
    if (!email) return message;
    const [before, after] = message.split(email);
    return (
      <>
        {before}
        <span className="font-medium text-gray-900">{email}</span>
        {after}
      </>
    );
  }, [activeGroup, t]);

  const copyToClipboard = useCopyToClipboard();

  const handleCopyPublisherEmail = useCallback(async () => {
    const email = activeGroup?.publication_contact_email;
    if (!email) {
      close();
      return;
    }
    await copyToClipboard(email);
    close();
  }, [activeGroup, copyToClipboard, close]);

  const handleConfirmSubmit = useCallback(async () => {
    if (modal?.kind !== 'confirm') return;

    // `resend-invite` (invited seats) and `reinvite` (removed/declined) share
    // one endpoint: Rails' ResendInvitation branches on seat status. The UI
    // distinction lives in the confirm copy ("Resend invite?" vs "Send invite?")
    // and the toast copy below — the wire call is identical.
    const handlerByKind: Record<GroupConfirmKind, BulkSeatActionHandler | undefined> = {
      remove: onBulkRemoveSeats,
      'cancel-invite': onBulkCancelInvites,
      'resend-invite': onBulkResendInvitations,
      reinvite: onBulkResendInvitations,
    };
    const handler = handlerByKind[modal.confirmKind];

    if (!handler) {
      toast(`"${modal.confirmKind}" is not yet supported`);
      close();
      return;
    }

    setIsConfirmSubmitting(true);
    try {
      const result = await handler(modal.groupId, modal.seatIds);
      // Refetch on ANY non-transport failure too — partial-success means some
      // seats moved; the snapshot would be stale otherwise.
      const shouldRefetch = result.ok || (result.results?.some((r) => r.success) ?? false);
      if (shouldRefetch) onMutationSuccess?.();

      if (!result.ok || !result.results) {
        toast.error(result.error ?? toastCopy(modal.confirmKind).failureGeneric);
        return;
      }

      const successCount = result.results.filter((r) => r.success).length;
      const failureCount = result.results.length - successCount;
      const copy = toastCopy(modal.confirmKind);

      if (failureCount === 0) {
        toast.success(copy.success(successCount));
        close();
      } else if (successCount > 0) {
        // Partial — close (the successes did land) but surface which one(s) failed.
        const firstFailure = result.results.find((r) => !r.success);
        toast.error(copy.partial(successCount, failureCount, firstFailure?.error));
        close();
      } else {
        // All failed — keep the modal open so the user can retry or dismiss.
        const firstFailure = result.results.find((r) => !r.success);
        toast.error(copy.allFailed(failureCount, firstFailure?.error));
      }
    } finally {
      setIsConfirmSubmitting(false);
    }
  }, [modal, onBulkRemoveSeats, onBulkCancelInvites, onBulkResendInvitations, onMutationSuccess, close]);

  /**
   * Email-invite intent — the authoritative entry point for the invite-by-email
   * modal's primary CTA (both the within-capacity "Send invites" and the
   * over-capacity "Add seats and invite" labels route here). The modal's overage
   * math is a client-side ESTIMATE off the loaded payload; before we invite OR
   * buy seats we re-resolve against the server (`seats/validation`):
   *   - `available` is the deduped, valid, not-already-seated list — so occupied
   *     addresses can't inflate the seat purchase.
   *   - `remaining_seats` is fresh capacity — so a stale snapshot can't under-buy
   *     and silently drop overflow after payment.
   * Net invariant: seats bought == genuinely-invitable addresses that don't
   * already fit. Rails still returns a per-email envelope on invite, so we keep
   * the all-success / partial / all-failure toast handling.
   */
  const handleEmailInviteIntent = useCallback(
    async (groupId: string, emails: string[]) => {
      // Don't stack a second purchase while one is awaiting payment.
      const group = groups.find((g) => g.id === groupId);
      if (group?.pending_seat_addition != null) {
        toast.error('A seat addition is already pending payment');
        return;
      }
      if (!onValidateSeats || !onCreateInvitations) {
        close();
        return;
      }
      setIsInviteSubmitting(true);
      try {
        const validation = await onValidateSeats(groupId, emails);
        if (!validation.ok || !validation.data) {
          toast.error(validation.error ?? 'Could not verify those addresses');
          return;
        }
        const { available, remaining_seats } = validation.data;
        if (available.length === 0) {
          toast.error('Those addresses are already invited or invalid');
          return;
        }

        const extraSeats = Math.max(0, available.length - remaining_seats);
        if (extraSeats > 0) {
          if (group?.custom_deal) {
            toast.error("This group doesn't have enough open seats. Contact your publisher to add more seats.");
            return;
          }
          // Authoritative over-capacity: buy exactly the shortfall and carry the
          // deduped list into the confirm modal for after-payment invite.
          setModal({ kind: 'addSeatsAndInvite', groupId, emails: available, extraSeats });
          return;
        }

        // Fits now — invite the deduped list.
        const result = await onCreateInvitations(groupId, available);
        if (!result.ok || !result.data) {
          toast.error(result.error ?? 'Failed to send invitations');
          return;
        }
        const { invitations, dropped } = result.data;
        const successCount = invitations.filter((i) => i.success).length;
        const failureCount = invitations.length - successCount;
        if (failureCount === 0 && successCount > 0) {
          toast.success(successCount === 1 ? '1 invitation sent' : `${successCount} invitations sent`);
          close();
        } else if (successCount > 0 && failureCount > 0) {
          // Partial — surface the failure but close (the successful ones landed).
          toast.error(formatPartialFailureMessage(result));
          close();
        } else {
          // All failed — stay open so the user can edit the list.
          toast.error(formatPartialFailureMessage(result));
        }
        if (dropped > 0) {
          toast.error(
            `${dropped} ${dropped === 1 ? 'invite was' : 'invites were'} skipped — not enough remaining seats`,
          );
        }
        if (successCount > 0) onMutationSuccess?.();
      } finally {
        setIsInviteSubmitting(false);
      }
    },
    [onValidateSeats, onCreateInvitations, groups, close, onMutationSuccess],
  );

  const modals = (
    <>
      {modal?.kind === 'manageSeats' && (
        <ManageSeatsModal
          isOpen
          group={activeGroup}
          isLoading={isReductionSubmitting}
          onCancel={close}
          onOpenChange={(open) => {
            if (!open) close();
          }}
          onSubmit={handleManageSeatsSubmit}
          onSelectMembersToRemove={handleSelectMembersToRemove}
        />
      )}

      {modal?.kind === 'offPlatformManageSeats' && (
        <ConfirmActionModalElement
          element={{ type: 'confirmActionModal' }}
          isOpen
          isSubmitting={false}
          title={t('off_platform_seats_title')}
          message={offPlatformSeatsMessage}
          submitLabel={t('off_platform_seats_copy_cta')}
          cancelLabel={t('off_platform_seats_cancel')}
          onCancel={close}
          onSubmit={handleCopyPublisherEmail}
          onOpenChange={close}
        >
          {null}
        </ConfirmActionModalElement>
      )}

      {modal?.kind === 'selectMembersToRemove' && (
        <SelectMembersToRemoveAtRenewalModal
          isOpen
          group={activeGroup}
          targetSeatCount={modal.targetSeatCount}
          // swap: `modal.deficit` = `group.seats_terminating` captured at open
          // time, which may be stale if the groups list hasn't refetched yet.
          // Derive from the fresh candidates fetch instead so the counter always
          // matches what's actually loaded.
          deficit={pickerMode === 'swap' ? reductionInitialSelected.length : modal.deficit}
          seats={reductionPool}
          initialSelectedIds={reductionInitialSelected}
          isLoadingSeats={isLoadingCandidates}
          isLoading={isReductionSubmitting}
          onBack={() => setModal({ kind: 'manageSeats', groupId: modal.groupId })}
          onConfirm={handlePickerConfirm}
          onOpenChange={(open) => {
            if (!open) close();
          }}
        />
      )}

      {modal?.kind === 'confirmCancelReduction' && (
        <ConfirmActionModalElement
          element={{ type: 'confirmActionModal' }}
          isOpen
          isSubmitting={isReductionSubmitting}
          title="Cancel scheduled reduction?"
          message="Members set to lose access at renewal will keep their seats, and your subscription will renew at its current seat count."
          submitLabel="Cancel reduction"
          cancelLabel="Keep reduction"
          submitVariant="danger"
          onCancel={close}
          onSubmit={handleConfirmCancelReduction}
          onOpenChange={close}
        >
          {null}
        </ConfirmActionModalElement>
      )}

      {modal?.kind === 'cancelSubscription' && (
        <ConfirmActionModalElement
          element={{ type: 'confirmActionModal' }}
          isOpen
          isSubmitting={isCancelSubscriptionSubmitting}
          title={t('cancel_subscription_title')}
          message={t(cancelSubscriptionMessageKey)}
          submitLabel={t('cancel_subscription')}
          cancelLabel={t('keep_subscription')}
          submitVariant="danger"
          onCancel={close}
          onSubmit={handleConfirmCancelSubscription}
          onOpenChange={close}
        >
          {null}
        </ConfirmActionModalElement>
      )}

      {modal?.kind === 'inviteByEmail' && (
        <InviteByEmailModal
          isOpen
          group={activeGroup}
          isLoading={isInviteSubmitting}
          onCancel={close}
          onOpenChange={(open) => {
            if (!open) close();
          }}
          onSubmit={handleEmailInviteIntent}
          onAddSeatsAndInvite={(groupId, emails) => handleEmailInviteIntent(groupId, emails)}
          onManageSeats={activeGroup && !activeGroup.custom_deal ? () => openManageSeats(modal.groupId) : undefined}
          canAddSeats={activeGroup ? !activeGroup.custom_deal : true}
          translations={translations}
        />
      )}

      {modal?.kind === 'bulkInviteCsv' && (
        <BulkInviteCsvModal
          isOpen
          group={activeGroup}
          onCancel={close}
          onOpenChange={(open) => {
            if (!open) close();
          }}
          onSubmit={close}
          onAddSeatsAndInvite={handleAddSeatsAndInvite}
          uploadState={csvUpload.state}
          onStartUpload={handleStartBulkUpload}
          onClearUpload={csvUpload.clearUpload}
          onUploadError={csvUpload.setError}
          parsedEmails={[]}
          onManageSeats={activeGroup && !activeGroup.custom_deal ? () => openManageSeats(modal.groupId) : undefined}
          translations={translations}
        />
      )}

      {modal?.kind === 'addSeatsAndInvite' && (
        <AddSeatsAndInviteConfirmModal
          isOpen
          group={activeGroup}
          extraSeats={modal.extraSeats}
          emails={modal.emails}
          isLoading={isSeatAdditionSubmitting}
          onCancel={close}
          onOpenChange={(open) => {
            if (!open) close();
          }}
          onSubmit={handleAddSeatsAndInviteSubmit}
        />
      )}

      {modal?.kind === 'confirm' && confirmCopy && (
        <ConfirmActionModalElement
          element={{ type: 'confirmActionModal' }}
          isOpen
          isSubmitting={isConfirmSubmitting}
          title={confirmCopy.title}
          message={confirmCopy.message}
          submitLabel={confirmCopy.submitLabel}
          cancelLabel={confirmCopy.cancelLabel}
          submitVariant={confirmCopy.submitVariant}
          onCancel={close}
          onSubmit={handleConfirmSubmit}
          onOpenChange={close}
        >
          {null}
        </ConfirmActionModalElement>
      )}
    </>
  );

  return {
    openManageSeats,
    openInviteByEmail,
    openBulkInviteCsv,
    openModifyReduction,
    openCancelReduction,
    openCancelSubscription,
    openConfirm,
    modals,
  };
}
